import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Award,
  Calendar,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  BarChart3,
  Layers,
  Plus,
  Play,
  X,
  ChevronRight,
  FileText,
  ClipboardCheck,
} from 'lucide-react';
import apiService from '../../services/api';

// ── helpers ──────────────────────────────────────────────────────────────────

const TOTAL_INDICATORS = 84;

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function progressPct(audit) {
  if (audit.status === 'submitted' || audit.status === 'reviewed') return 100;
  const cnt = audit.responses_count || 0;
  return Math.min(Math.round((cnt / TOTAL_INDICATORS) * 100), 99);
}

function statusMeta(audit) {
  const s = audit.status;
  if (s === 'submitted' || s === 'reviewed') return { label: 'Submitted', color: 'bg-green-100 text-green-700', dot: 'bg-green-500' };
  const pct = progressPct(audit);
  if (pct > 0) return { label: 'In Progress', color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' };
  return { label: 'Not Started', color: 'bg-yellow-100 text-yellow-700', dot: 'bg-yellow-400' };
}

// ── New Year Modal ────────────────────────────────────────────────────────────

function NewYearModal({ onClose, onCreate }) {
  const [year, setYear] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCreate = async () => {
    if (!/^\d{4}-\d{2,4}$/.test(year.trim())) {
      setError('Enter a valid academic year (e.g. 2027-28)');
      return;
    }
    setError('');
    setLoading(true);
    await onCreate(year.trim());
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 font-[poppins]">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#D67C2C' }}>
              <Plus className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">New SQAAF Assessment</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Academic Year <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="e.g. 2027-28"
              value={year}
              onChange={e => { setYear(e.target.value); setError(''); }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
            />
            {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
          </div>
          <div className="bg-orange-50 border border-orange-100 rounded-lg p-3 text-xs text-orange-700">
            A new SQAAF assessment will be created for <strong>{year || '…'}</strong>. You can take the quiz, review scores, and generate an AI action plan — all within this year's plan.
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={loading || !year.trim()}
            className="flex-1 py-2.5 text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            style={{ backgroundColor: loading || !year.trim() ? '#e0a070' : '#D67C2C' }}
          >
            {loading ? (
              <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Starting…</>
            ) : (
              <><Play className="w-4 h-4" />Start Assessment</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function SQAAFPlansManager() {
  const navigate = useNavigate();
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [toast, setToast] = useState(null);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ status: '', academicYear: '' });

  useEffect(() => {
    apiService.sqaaf.getAuditHistory()
      .then(res => {
        if (res?.audits) setAudits(res.audits);
      })
      .catch(() => setError('Failed to load SQAAF assessments. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleCreate = async (year) => {
    try {
      const user = JSON.parse(localStorage.getItem('user_info') || '{}');
      const schoolId = user.school_id || localStorage.getItem('school_id');
      await apiService.sqaaf.startAudit(schoolId, year);
      setShowModal(false);
      showToast(`SQAAF ${year} created — starting assessment!`);
      navigate(`/SQAAF/${encodeURIComponent(year)}`);
    } catch (err) {
      const msg = err?.message || '';
      if (msg.includes('already exists') || msg.includes('409')) {
        // Audit already exists for this year — just navigate
        setShowModal(false);
        navigate(`/SQAAF/${encodeURIComponent(year)}`);
      } else {
        showToast('Failed to create assessment', 'error');
      }
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const academicYears = [...new Set(audits.map(a => a.academic_year))];

  const filteredAudits = audits
    .filter(a => !filters.status || a.status === filters.status)
    .filter(a => !filters.academicYear || a.academic_year === filters.academicYear);

  // Stats
  const total = audits.length;
  const submitted = audits.filter(a => a.status === 'submitted' || a.status === 'reviewed').length;
  const inProgress = audits.filter(a => progressPct(a) > 0 && a.status !== 'submitted' && a.status !== 'reviewed').length;
  const avgScore = total > 0
    ? (audits.reduce((s, a) => s + (a.total_score || 0), 0) / total).toFixed(1)
    : '0.0';

  return (
    <>
      <div className="bg-white p-8 min-h-screen rounded-b-2xl rounded-tr-2xl shadow-xl font-[poppins]">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-8">

          {/* Error banner */}
          {error && (
            <div className="fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-3 bg-red-500 text-white">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
              <button onClick={() => setError(null)} className="ml-2 hover:opacity-80"><X className="w-4 h-4" /></button>
            </div>
          )}

          {/* Page Header */}
          <div className="pt-8 pb-8 text-center relative overflow-hidden">
            <div className="relative z-10">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ backgroundColor: '#D67C2C' }}>
                <Award className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-5xl font-bold bg-clip-text text-transparent mb-4" style={{ backgroundImage: 'linear-gradient(135deg, #D67C2C, #c06a25)' }}>
                SQAAF Assessments
              </h1>
              <p className="text-[#1D3469] text-xl font-medium max-w-3xl mx-auto px-4">
                School Quality Assessment & Accreditation Framework — one assessment per academic year
              </p>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-all duration-300">
              <div className="p-6 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Total Assessments</p>
                  <p className="text-3xl font-bold text-blue-600 mt-1">{total}</p>
                </div>
                <div className="p-3 rounded-full bg-blue-100">
                  <Layers className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-all duration-300">
              <div className="p-6 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Avg Quality Score</p>
                  <p className="text-3xl font-bold mt-1" style={{ color: '#D67C2C' }}>{avgScore}%</p>
                </div>
                <div className="p-3 rounded-full" style={{ backgroundColor: '#FEF0E7' }}>
                  <TrendingUp className="w-6 h-6" style={{ color: '#D67C2C' }} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-all duration-300">
              <div className="p-6 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Submitted</p>
                  <p className="text-3xl font-bold text-green-600 mt-1">{submitted}</p>
                </div>
                <div className="p-3 rounded-full bg-green-100">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-all duration-300">
              <div className="p-6 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">In Progress</p>
                  <p className="text-3xl font-bold text-purple-600 mt-1">{inProgress}</p>
                </div>
                <div className="p-3 rounded-full bg-purple-100">
                  <BarChart3 className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Assessments Table */}
          <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200">
            <div className="p-6 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#D67C2C' }}>
                    <ClipboardCheck className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">All SQAAF Assessments</h2>
                </div>
                <button
                  onClick={() => setShowModal(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-xl transition-colors shadow-sm hover:opacity-90"
                  style={{ backgroundColor: '#D67C2C' }}
                >
                  <Plus className="w-4 h-4" />
                  New Assessment Year
                </button>
              </div>
            </div>

            {/* Filter Bar */}
            {!loading && audits.length > 0 && (
              <div className="px-6 py-4 border-b border-gray-100 bg-white grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Status</label>
                  <select
                    value={filters.status}
                    onChange={e => handleFilterChange('status', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                  >
                    <option value="">All Statuses</option>
                    <option value="in_progress">In Progress</option>
                    <option value="submitted">Submitted</option>
                    <option value="reviewed">Reviewed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Academic Year</label>
                  <select
                    value={filters.academicYear}
                    onChange={e => handleFilterChange('academicYear', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                  >
                    <option value="">All Years</option>
                    {academicYears.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {loading ? (
              <div className="text-center py-16">
                <div className="w-10 h-10 border-4 border-gray-200 rounded-full animate-spin mx-auto mb-4" style={{ borderTopColor: '#D67C2C' }} />
                <p className="text-gray-500 text-sm">Loading assessments…</p>
              </div>
            ) : audits.length === 0 ? (
              <div className="text-center py-16">
                <Award className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium text-gray-500 mb-1">No assessments yet</p>
                <p className="text-sm text-gray-400 mb-6">Start your first SQAAF assessment to evaluate school quality</p>
                <button
                  onClick={() => setShowModal(true)}
                  className="inline-flex items-center gap-2 px-6 py-2.5 text-white text-sm font-medium rounded-xl hover:opacity-90 transition-colors"
                  style={{ backgroundColor: '#D67C2C' }}
                >
                  <Plus className="w-4 h-4" />
                  Start First Assessment
                </button>
              </div>
            ) : filteredAudits.length === 0 ? (
              <div className="text-center py-16">
                <Award className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium text-gray-500 mb-1">No assessments match your filters</p>
                <p className="text-sm text-gray-400">Try adjusting the status or academic year filter</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Academic Year</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progress</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quality Score</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Updated</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredAudits.map(audit => {
                      const meta = statusMeta(audit);
                      const pct = progressPct(audit);
                      const score = audit.total_score ? audit.total_score.toFixed(1) + '%' : '—';

                      return (
                        <tr key={audit.audit_id} className="hover:bg-gray-50 transition-colors">
                          {/* Year */}
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 bg-orange-100">
                                <Calendar className="w-4 h-4 text-[#D67C2C]" />
                              </div>
                              <span className="text-sm font-semibold text-gray-900">{audit.academic_year}</span>
                            </div>
                          </td>

                          {/* Status */}
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${meta.color}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                              {meta.label}
                            </span>
                          </td>

                          {/* Progress bar */}
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2 min-w-[130px]">
                              <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all"
                                  style={{
                                    width: `${pct}%`,
                                    backgroundColor: pct >= 100 ? '#22c55e' : pct >= 60 ? '#3b82f6' : pct > 0 ? '#f97316' : '#facc15',
                                  }}
                                />
                              </div>
                              <span className="text-xs text-gray-600 whitespace-nowrap">
                                {audit.responses_count || 0}/{TOTAL_INDICATORS}
                              </span>
                            </div>
                          </td>

                          {/* Score */}
                          <td className="px-6 py-4">
                            <span className={`text-sm font-semibold ${audit.total_score ? 'text-[#D67C2C]' : 'text-gray-400'}`}>
                              {score}
                            </span>
                          </td>

                          {/* Created */}
                          <td className="px-6 py-4 text-sm text-gray-500">{fmtDate(audit.created_at)}</td>

                          {/* Last updated */}
                          <td className="px-6 py-4 text-sm text-gray-500">{fmtDate(audit.updated_at)}</td>

                          {/* Actions */}
                          <td className="px-6 py-4">
                            {audit.status === 'submitted' || audit.status === 'reviewed' ? (
                              <button
                                onClick={() => navigate(`/SQAAF/${encodeURIComponent(audit.academic_year)}`)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-colors"
                                style={{ backgroundColor: '#D67C2C' }}
                              >
                                <FileText className="w-3.5 h-3.5" />
                                View Report
                              </button>
                            ) : (
                              <button
                                onClick={() => navigate(`/SQAAF/${encodeURIComponent(audit.academic_year)}`)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-colors"
                                style={{ backgroundColor: '#D67C2C' }}
                              >
                                <Play className="w-3.5 h-3.5" />
                                {pct > 0 ? 'Continue' : 'Start'}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* New Year Modal */}
        {showModal && (
          <NewYearModal
            onClose={() => setShowModal(false)}
            onCreate={handleCreate}
          />
        )}

        {/* Toast */}
        {toast && (
          <div
            className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-sm font-medium text-white transition-all ${toast.type === 'error' ? 'bg-red-500' : 'bg-green-600'}`}
            style={{ animation: 'slideInRight 0.3s ease-out' }}
          >
            {toast.type === 'error' ? <AlertCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
            {toast.msg}
          </div>
        )}
      </div>
    </>
  );
}
